import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { Wordmark } from "./Wordmark";

const SESSION_KEY = "btd_access_granted_at";
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const ACCESS_CODE = "giraffe";
const BACKDROP =
  "https://images.unsplash.com/photo-1536392706976-e486e2ba97af?auto=format&fit=crop&w=2000&q=70";

const EASE = [0.76, 0, 0.24, 1];

const hasValidSession = () => {
  try {
    const ts = window.localStorage.getItem(SESSION_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) < TTL_MS;
  } catch (e) {
    return false;
  }
};

/**
 * AccessGate - a client-side "Institutional Access" gate for pitches.
 * NOTE: not real security; a hardcoded code + localStorage session (24h).
 */
export const AccessGate = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [granted, setGranted] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    setGranted(hasValidSession());
    setReady(true);
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (code.trim().toLowerCase() === ACCESS_CODE) {
      try {
        window.localStorage.setItem(SESSION_KEY, String(Date.now()));
      } catch (err) {
        /* ignore storage errors */
      }
      setError(false);
      setGranted(true);
    } else {
      setError(true);
      setCode("");
    }
  };

  // Avoid a flash of the gate before we know the session state
  if (!ready) return null;

  // Once granted, unmount the gate entirely - no AnimatePresence, no motion
  // wrapper, no lingering DOM that could intercept clicks on the site behind.
  if (granted) return <>{children}</>;

  return (
    <motion.div
      data-testid="access-gate"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[color:var(--bt-bg)] px-6"
    >
        {/* Blurred luxe backdrop */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={BACKDROP}
            alt=""
            aria-hidden
            className="h-full w-full object-cover scale-110 blur-2xl opacity-40"
          />
          <div className="absolute inset-0 bg-[color:var(--bt-bg)]/80" />
          <div className="bt-grain" aria-hidden />
        </div>

        {/* Frame lines */}
        <div className="pointer-events-none absolute inset-y-0 left-6 md:left-10 w-px bg-[color:var(--bt-gold-line)]" />
        <div className="pointer-events-none absolute inset-y-0 right-6 md:right-10 w-px bg-[color:var(--bt-gold-line)]" />

        {/* Modal */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          data-testid="access-modal"
          className="relative w-full max-w-[520px] border border-[color:var(--bt-gold-line)] bg-[color:var(--bt-bg-2)]/80 backdrop-blur-xl"
        >
          <div className="px-8 md:px-12 py-12 md:py-14 flex flex-col items-center text-center">
            <Wordmark size="md" dataTestId="access-wordmark" />

            <div className="mt-10 flex items-center gap-3 text-[color:var(--bt-gold)]">
              <Lock size={14} />
              <span className="text-[11px] tracking-widest-brand uppercase">
                Institutional Access
              </span>
            </div>

            <h1 className="mt-5 font-serif-display text-4xl md:text-5xl leading-[1] tracking-tight text-[color:var(--bt-ink)]">
              By invitation, and{" "}
              <span className="italic font-light text-[color:var(--bt-gold)]">
                access code.
              </span>
            </h1>

            <p className="mt-5 text-[color:var(--bt-ink-dim)] text-base leading-relaxed max-w-sm">
              This framework is being shared privately. Kindly enter your access
              code to view the portal.
            </p>

            <form
              onSubmit={onSubmit}
              data-testid="access-form"
              className="mt-10 w-full"
            >
              <div
                className={`relative border-b transition-colors ${
                  error
                    ? "border-[#b3543f]"
                    : "border-[color:var(--bt-gold-line)] focus-within:border-[color:var(--bt-gold)]"
                }`}
              >
                <label className="block text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-2 text-left">
                  Access Code
                </label>
                <input
                  data-testid="access-code-input"
                  type="password"
                  autoFocus
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (error) setError(false);
                  }}
                  placeholder="••••••••"
                  className="w-full bg-transparent py-3 text-2xl font-serif-display text-[color:var(--bt-ink)] placeholder:text-[color:var(--bt-ink-faint)] focus:outline-none text-center tracking-[0.3em]"
                />
              </div>

              <AnimatePresence>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    data-testid="access-error"
                    className="mt-4 text-[11px] tracking-widest-brand uppercase text-[#d0755f]"
                  >
                    That code is not recognised. Kindly try again.
                  </motion.p>
                )}
              </AnimatePresence>

              <button
                type="submit"
                data-testid="access-submit"
                className="bt-btn w-full mt-8"
              >
                Enter the Portal <ArrowRight size={14} />
              </button>
            </form>

            <div className="mt-8 text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
              Access is remembered on this device for 24 hours.
            </div>
          </div>
        </motion.div>
      </motion.div>
  );
};

export default AccessGate;
